import React, { useEffect } from 'react'
import { MfaError, MFAMethod } from '@mobi/api-types/src/mfa'
import { useMfaUpdateAuthMutation } from '../Hooks/useMfaUpdateAuthMutation'
import { useNavigate, useParams } from 'react-router'
import { handleMfaError, mfaCodeText } from '../helpers'
import { mapMFAMethodToAttributeName } from '../utils'
import {
  startCognitoAttributeVerification,
  useMfaCompleteVerification,
} from '../Hooks/useMfaVerification'
import { VerificationCodeForm } from '../Components/VerificationCodeForm'
import { MainContainer } from '@core/Components/Containers'
import { SettingsHeader } from '@core/Areas/Settings/SettingsHeader'
import {
  MfaCodePageHeader,
  MfaCodePageText,
  MfaVerificationCodePageContainer,
} from './VerificationCodePage.styles'
import { useLogon } from '@core/Utils/hooks'
import { useCognitoUser } from '@core/Data/Account/useCognitoUser'

interface VerificationCodeContentProps {
  preferredFactor: MFAMethod
  accountNumber: number
}

export function clearVerificationCodePageSessionState() {
  sessionStorage.removeItem('verificationCodePage:Error')
  sessionStorage.removeItem('verificationCodePage:ExpiryTime')
}

export const VerificationCodePage: React.FC = () => {
  const { preferredFactor } = useParams()
  const { accountNumber } = useLogon()
  const validatedPreferredFactor = validatePreferredFactor(preferredFactor ?? 'SMS') ?? 'SMS'

  return accountNumber ? (
    <VerificationCodeContent
      preferredFactor={validatedPreferredFactor}
      accountNumber={accountNumber}
    />
  ) : null
}

const VerificationCodeContent: React.FC<VerificationCodeContentProps> = ({
  preferredFactor,
  accountNumber,
}) => {
  const navigate = useNavigate()
  const { mutateAsync: updateMfaPreference } = useMfaUpdateAuthMutation()
  const { data: cognitoUser } = useCognitoUser(accountNumber)
  const { completeVerification } = useMfaCompleteVerification()

  /*
   * The below is used to preserve error & code expiry state across navigation to contact details page.
   * This allows us to restore the error message when user returns using the back navigation.
   */

  const preservedError = sessionStorage.getItem('verificationCodePage:Error')
  const preservedExpiryTime = Number(
    sessionStorage.getItem('verificationCodePage:ExpiryTime') ?? '0'
  )

  useEffect(() => {
    if (preservedError) {
      sessionStorage.removeItem('verificationCodePage:Error')
    }
    if (preservedExpiryTime) {
      sessionStorage.removeItem('verificationCodePage:ExpiryTime')
    }
  }, [preservedError, preservedExpiryTime])

  const handleVerify = async (code: string): Promise<void> => {
    try {
      const verificationResult = await completeVerification({
        attributeName: mapMFAMethodToAttributeName(preferredFactor),
        code: code,
      })

      if (verificationResult) {
        await updateMfaPreference({
          preferredFactor: preferredFactor,
        })
        navigate('/settings/mfa/', { replace: true })
      }
    } catch (error) {
      if (error && typeof error == 'object' && 'status' in error && error.status === 403) {
        // rethrow and have it handled by the component that calls the handler
        throw error
      } else {
        handleMfaError(error as MfaError, 'complete')
      }
    }
  }

  const handleResend = async (): Promise<void> => {
    try {
      await startCognitoAttributeVerification({
        attributeName: mapMFAMethodToAttributeName(preferredFactor),
      })
      return
    } catch (error) {
      handleMfaError(error as MfaError, 'verification')
    }
  }

  const handleEditContact = (error: string, expiryTime: number) => {
    sessionStorage.setItem('verificationCodePage:Error', error)
    sessionStorage.setItem('verificationCodePage:ExpiryTime', expiryTime.toString())
    navigate('/account/contactdetails')
  }

  if (cognitoUser === undefined) {
    return (
      <MainContainer data-tid-mfa-verification-container>
        <MfaCodePageHeader>Verify your contact details</MfaCodePageHeader>
        <p>There was an error trying to verify your contact details.</p>
      </MainContainer>
    )
  }

  const contactDetail =
    preferredFactor === 'SMS'
      ? (cognitoUser.userAttributes.phone_number ?? '')
      : (cognitoUser.userAttributes.email ?? '')

  return (
    <MainContainer data-tid-mfa-verification-page>
      <SettingsHeader title='Settings' />
      <MfaVerificationCodePageContainer>
        <MfaCodePageHeader>Verify your contact details</MfaCodePageHeader>
        <MfaCodePageText>{mfaCodeText(preferredFactor, contactDetail)}</MfaCodePageText>
        <VerificationCodeForm
          onVerify={handleVerify}
          onResend={handleResend}
          onEditContact={handleEditContact}
          initialError={preservedError}
          initialExpiryTime={preservedExpiryTime > 0 ? preservedExpiryTime : undefined}
        />
      </MfaVerificationCodePageContainer>
    </MainContainer>
  )
}

function validatePreferredFactor(factorString: string): MFAMethod | undefined {
  switch (factorString) {
    case 'None':
    case 'SMS':
    case 'Email':
    case 'SoftwareToken':
    case 'Unknown':
      return factorString as MFAMethod
    default:
      return undefined
  }
}
